'use client';

import * as CANNON from 'cannon-es';
import { useCallback, useRef } from 'react';

import type { Layer } from '@/app/(football)/[team]/mini-game/_components/Section/GameSection/BlockStackingGame/types';

/**
 * Physics world management hook
 * Manages physics simulation using Cannon.js
 */
export function usePhysicsWorld() {
  const worldRef = useRef<CANNON.World | undefined>(undefined);

  const initializePhysicsWorld = useCallback(() => {
    const world = new CANNON.World();
    world.gravity.set(0, -10, 0);
    world.broadphase = new CANNON.NaiveBroadphase();
    (world.solver as CANNON.GSSolver).iterations = 40;
    worldRef.current = world;
    return world;
  }, []);

  const createPhysicsBody = useCallback(
    (x: number, y: number, z: number, width: number, depth: number, boxHeight: number, falls: boolean) => {
      const world = worldRef.current;
      if (!world) throw new Error('Physics world is not initialized');

      const shape = new CANNON.Box(new CANNON.Vec3(width / 2, boxHeight / 2, depth / 2));
      let mass = falls ? 5 : 0;

      const originalBoxSize = 3;
      mass *= width / originalBoxSize;
      mass *= depth / originalBoxSize;

      const body = new CANNON.Body({ mass, shape });
      body.position.set(x, y, z);
      world.addBody(body);

      return body;
    },
    [],
  );

  const updatePhysics = useCallback((timePassed: number, overhangs: Layer[]) => {
    const world = worldRef.current;
    if (!world) return;

    world.step(timePassed / 1000);

    overhangs.forEach((element) => {
      element.threejs.position.copy(element.cannonjs.position);
      element.threejs.quaternion.copy(element.cannonjs.quaternion);
    });
  }, []);

  const clearPhysicsWorld = useCallback(() => {
    const world = worldRef.current;
    if (!world) return;

    while (world.bodies.length > 0) {
      world.removeBody(world.bodies[0]);
    }
    worldRef.current = undefined;
  }, []);

  const removePhysicsBody = useCallback((body: CANNON.Body) => {
    const world = worldRef.current;
    if (world && body) {
      world.removeBody(body);
    }
  }, []);

  const updatePhysicsBodyShape = useCallback((body: CANNON.Body, width: number, height: number, depth: number) => {
    const shape = new CANNON.Box(new CANNON.Vec3(width / 2, height / 2, depth / 2));
    body.shapes = [];
    body.addShape(shape);
  }, []);

  return {
    worldRef,
    initializePhysicsWorld,
    createPhysicsBody,
    updatePhysics,
    clearPhysicsWorld,
    removePhysicsBody,
    updatePhysicsBodyShape,
  };
}
